import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { useRepairOrder } from '../../hooks/useRepairOrders';
import { useContacts } from '../../hooks/useContacts';
import { useInventory } from '../../hooks/useInventory';
import { useAuth } from '../../context/AuthContext';
import { repairOrdersService, RepairOrderFormData, RepairOrderItemFormData } from '../../services/repairOrdersService';
import { inventoryService } from '../../services/inventoryService';
import { formatCurrency } from '../../utils/currency';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import SearchableSelect from '../../components/ui/SearchableSelect';
import ItemSelectionModal from '../../components/ui/ItemSelectionModal';
import NonInventoryItemModal from '../../components/ui/NonInventoryItemModal';
import { WarningModal } from '../../components/ui/ItemSelectionModal';
import Breadcrumb from '../../components/ui/Breadcrumb';

const RepairOrderForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { order: existingOrder, loading: orderLoading } = useRepairOrder(id || null);
  const { contacts, loading: contactsLoading } = useContacts();
  const { items: inventoryItems, loading: inventoryLoading } = useInventory();
  const { businessId } = useAuth();

  // Form state
  const [repairOrderNumber, setRepairOrderNumber] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [deviceBrand, setDeviceBrand] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  const [deviceSerial, setDeviceSerial] = useState('');
  const [deviceProblem, setDeviceProblem] = useState('');
  const [deviceDiagnosis, setDeviceDiagnosis] = useState('');
  const [status, setStatus] = useState<'pending' | 'process' | 'completed'>('pending');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [completedDate, setCompletedDate] = useState('');
  const [technicianId, setTechnicianId] = useState('');
  const [items, setItems] = useState<RepairOrderItemFormData[]>([]);

  // UI state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isNonInventoryModalOpen, setIsNonInventoryModalOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [stockValidationResults, setStockValidationResults] = useState<any[]>([]);

  // Filter contacts
  const customers = contacts.filter(contact => contact.type === 'customer');
  const employees = contacts.filter(contact => contact.type === 'employee');

  // Get technicians (users) for the business
  const [technicians, setTechnicians] = useState<any[]>([]);
  useEffect(() => {
    const fetchTechnicians = async () => {
      if (businessId) {
        try {
          const { data, error } = await supabase
            .from('users')
            .select('*')
            .eq('business_id', businessId);

          if (!error && data) {
            setTechnicians(data);
          }
        } catch (err) {
          console.error('Error fetching technicians:', err);
        }
      }
    };

    fetchTechnicians();
  }, [businessId]);

  // Generate repair order number for new orders
  useEffect(() => {
    if (!id && !existingOrder) {
      // Generate a new repair order number for new orders
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const generatedNumber = `RO-${timestamp}${random}`;
      setRepairOrderNumber(generatedNumber);
    }
  }, [id, existingOrder]);

  // Initialize form with existing order data
  useEffect(() => {
    if (existingOrder) {
      console.log('Loading existing order:', existingOrder);
      console.log('Existing order items:', existingOrder.items);

      setRepairOrderNumber(existingOrder.repair_order_number);
      setCustomerId(existingOrder.customer_id);
      setDeviceBrand(existingOrder.device_brand || '');
      setDeviceModel(existingOrder.device_model || '');
      setDeviceSerial(existingOrder.device_serial || '');
      setDeviceProblem(existingOrder.device_problem || '');
      setDeviceDiagnosis(existingOrder.device_diagnosis || '');
      // Handle status conversion for existing orders with old status values
      const currentStatus = existingOrder.status;
      if (currentStatus === 'invoiced' || currentStatus === 'cancelled') {
        setStatus('completed'); // Default to completed for old statuses
      } else {
        setStatus(currentStatus as 'pending' | 'process' | 'completed');
      }
      setEntryDate(existingOrder.entry_date);
      setCompletedDate(existingOrder.completed_date || '');
      setTechnicianId(existingOrder.technician_id || '');

      // For existing items, we need to fetch current available stock and adjust for already allocated quantities
      const loadExistingItems = async () => {
        console.log('Loading existing items for repair order:', existingOrder.id);

        if (!existingOrder.items || existingOrder.items.length === 0) {
          console.log('No existing items found for repair order');
          setItems([]);
          return;
        }

        const loadedItems = await Promise.all(
          existingOrder.items.map(async (item) => {
            console.log('Processing existing item:', item);
            let availableStock: number | undefined;

            // Fetch current available stock for inventory items
            if (item.item_id && typeof item.item_id === 'string' && !item.item_id.startsWith('non-inventory')) {
              try {
                const { item: inventoryItem } = await inventoryService.getInventoryItemById(item.item_id);
                // Add back the quantity already allocated to this repair order
                // This gives the effective available stock for editing
                availableStock = (inventoryItem?.quantity || 0) + item.quantity;
                console.log('Available stock for item', item.item_id, ':', availableStock);
              } catch (error) {
                console.warn('Failed to fetch available stock for item:', item.item_id);
              }
            }

            return {
              item_id: item.item_id,
              item_name: item.item_name,
              quantity: item.quantity,
              unit_price: item.unit_price,
              total_price: item.total_price,
              item_type: (item.item_id && typeof item.item_id === 'string' && item.item_id.startsWith('non-inventory') ? 'non_inventory' : 'inventory') as 'inventory' | 'non_inventory',
              sku: (item as any).sku || '',
              available_stock: availableStock,
            };
          })
        );

        console.log('Loaded items:', loadedItems);
        setItems(loadedItems);
      };

      loadExistingItems();
    } else {
      console.log('No existing order found');
    }
  }, [existingOrder]);

  // Handle status change to show/hide completed date
  const handleStatusChange = (newStatus: typeof status) => {
    setStatus(newStatus);
    if (newStatus === 'completed' && !completedDate) {
      setCompletedDate(new Date().toISOString().split('T')[0]);
    } else if (newStatus !== 'completed') {
      setCompletedDate('');
    }
  };

  // Add a new item to the repair order
  const addItem = () => {
    setItems([
      ...items,
      {
        item_id: '',
        item_name: '',
        quantity: 1,
        unit_price: 0,
        total_price: 0,
        item_type: 'non_inventory',
        // available_stock is undefined for manually added non-inventory items
      }
    ]);
  };

  // Update an item in the repair order
  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...items];

    if (field === 'item_id') {
      // Find the selected item
      const selectedItem = inventoryItems.find(item => item.id === value);
      if (selectedItem) {
        newItems[index] = {
          ...newItems[index],
          item_id: value,
          item_name: selectedItem.name,
          unit_price: selectedItem.average_cost,
          total_price: selectedItem.average_cost * newItems[index].quantity,
        };
      }
    } else if (field === 'quantity' || field === 'unit_price') {
      newItems[index] = {
        ...newItems[index],
        [field]: value,
      };

      // Recalculate total price
      newItems[index].total_price = newItems[index].quantity * newItems[index].unit_price;
    } else {
      newItems[index] = {
        ...newItems[index],
        [field]: value,
      };
    }

    setItems(newItems);
  };

  // Remove an item from the repair order
  const removeItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  // Handle item selection from modal
  const handleItemSelect = (selectedItem: any) => {
    const newItem: RepairOrderItemFormData = {
      item_id: selectedItem.id,
      item_name: selectedItem.name,
      quantity: 1,
      unit_price: selectedItem.average_cost,
      total_price: selectedItem.average_cost,
      item_type: 'inventory',
      sku: selectedItem.sku || '',
      available_stock: selectedItem.quantity || 0, // Add available stock info
    };

    setItems([...items, newItem]);
  };

  // Handle non-inventory item addition
  const handleNonInventoryItemAdd = (itemData: { name: string; quantity: number; unit_price: number }) => {
    const newItem: RepairOrderItemFormData = {
      item_id: `non-inventory-${Date.now()}`, // Generate unique ID for non-inventory items
      item_name: itemData.name,
      quantity: itemData.quantity,
      unit_price: itemData.unit_price,
      total_price: itemData.quantity * itemData.unit_price,
      item_type: 'non_inventory',
      // available_stock is undefined for non-inventory items
    };

    setItems([...items, newItem]);
  };

  // Calculate total amount
  const totalAmount = items.reduce((sum, item) => sum + item.total_price, 0);

  // Validate stock before saving
  const validateStockBeforeSave = async () => {
    if (items.length === 0) return true;

    try {
      // Pass existing order ID for editing context
      const { results, error } = await repairOrdersService.validateStock(items, id || undefined);

      if (error) {
        setError('Failed to validate stock availability');
        return false;
      }

      const insufficientItems = results.filter(item => item.status === 'insufficient');
      if (insufficientItems.length > 0) {
        setStockValidationResults(results);
        setWarningMessage(
          `The following items have insufficient stock:\n${insufficientItems.map(item =>
            `${item.item_name}: Requested ${item.requested_quantity}, Available ${item.available_quantity}`
          ).join('\n')}\n\nDo you want to proceed with reduced quantities?`
        );
        setIsWarningModalOpen(true);
        return false;
      }

      return true;
    } catch (err) {
      setError('Failed to validate stock');
      return false;
    }
  };

  // Handle warning modal confirm
  const handleWarningConfirm = () => {
    setIsWarningModalOpen(false);
    // Adjust quantities to available stock
    const adjustedItems = items.map(item => {
      const validation = stockValidationResults.find(v => v.item_id === item.item_id);
      if (validation && validation.status === 'insufficient') {
        return {
          ...item,
          quantity: validation.available_quantity,
          total_price: validation.available_quantity * item.unit_price,
        };
      }
      return item;
    });
    setItems(adjustedItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);

      // Validate required fields
      if (!customerId) {
        throw new Error('Please select a customer');
      }

      if (!deviceBrand || !deviceModel || !deviceSerial || !deviceProblem) {
        throw new Error('Please fill in all device information fields');
      }

      if (status === 'completed' && !completedDate) {
        throw new Error('Completed date is required when status is Completed');
      }

      // Validate stock
      const stockValid = await validateStockBeforeSave();
      if (!stockValid) {
        setLoading(false);
        return;
      }

      const orderData: RepairOrderFormData = {
        repair_order_number: repairOrderNumber,
        customer_id: customerId,
        device_brand: deviceBrand,
        device_model: deviceModel,
        device_serial: deviceSerial,
        device_problem: deviceProblem,
        device_diagnosis: deviceDiagnosis,
        status,
        entry_date: entryDate,
        completed_date: completedDate || undefined,
        technician_id: technicianId || undefined,
        items,
      };

      // Debug logging
      console.log('Submitting repair order:', {
        businessId,
        orderData,
        itemsCount: items.length,
        hasItems: items.length > 0,
        itemDetails: items.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          item_type: item.item_type,
          quantity: item.quantity
        }))
      });

      // Ensure business ID is available
      if (!businessId) {
        throw new Error('Business information not available. Please refresh the page and try again.');
      }

      let result;
      if (id) {
        // Update existing repair order
        console.log('Updating existing repair order:', id);
        result = await repairOrdersService.updateRepairOrder(id, orderData, businessId);
      } else {
        // Create new repair order
        console.log('Creating new repair order');
        result = await repairOrdersService.createRepairOrder(orderData, businessId);
      }

      console.log('Repair order operation result:', result);

      if (result.error) {
        throw result.error;
      }

      navigate('/repair-orders');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save repair order');
    } finally {
      setLoading(false);
    }
  };

  if (orderLoading || contactsLoading || inventoryLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Repair Orders', href: '/repair-orders' },
    { label: id ? 'Edit Repair Order' : 'New Repair Order', current: true }
  ];

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {id ? 'Edit Repair Order' : 'Create New Repair Order'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          {/* Repair Order Details Section */}
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">Repair Order Details</h3>

            {/* First Row - 3 columns */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-6">
              <Input
                label="Repair Order Number"
                type="text"
                value={repairOrderNumber}
                onChange={(e) => setRepairOrderNumber(e.target.value)}
                placeholder="RO-XXXXXX"
                required
              />

              <Input
                label="Entry Date"
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                required
              />

              {status === 'completed' && (
                <Input
                  label="Completed Date"
                  type="date"
                  value={completedDate}
                  onChange={(e) => setCompletedDate(e.target.value)}
                  required
                />
              )}
            </div>

            {/* Second Row - 3 columns */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-8">
              <SearchableSelect
                label="Customer"
                value={customerId}
                onChange={setCustomerId}
                options={customers.map(customer => ({
                  value: customer.id,
                  label: customer.name,
                  sublabel: customer.contact_id || customer.email || customer.phone || ''
                }))}
                placeholder="Search customers..."
                required
              />

              <Select
                label="Technician"
                value={technicianId}
                onChange={(e) => setTechnicianId(e.target.value)}
                options={[
                  { value: '', label: 'Unassigned' },
                  ...employees.map(employee => ({
                    value: employee.id,
                    label: employee.name,
                  }))
                ]}
              />

              <Select
                label="Status"
                value={status}
                onChange={(e) => handleStatusChange(e.target.value as typeof status)}
                options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'process', label: 'In Process' },
                  { value: 'completed', label: 'Completed' },
                ]}
                required
              />
            </div>
          </div>

          {/* Device Information Section */}
          <div className="px-4 py-5 sm:px-6 border-t border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Device Information</h3>

            {/* First Row - 3 columns */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-6">
              <Input
                label="Device Brand"
                type="text"
                value={deviceBrand}
                onChange={(e) => setDeviceBrand(e.target.value)}
                placeholder="e.g., Apple, Samsung"
                required
              />

              <Input
                label="Device Model"
                type="text"
                value={deviceModel}
                onChange={(e) => setDeviceModel(e.target.value)}
                placeholder="e.g., iPhone 12, Galaxy S21"
                required
              />

              <Input
                label="Device IMEI"
                type="text"
                value={deviceSerial}
                onChange={(e) => setDeviceSerial(e.target.value)}
                placeholder="Enter device IMEI"
                required
              />
            </div>

            {/* Second Row - 2 columns */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Problem *
                </label>
                <textarea
                  className="input-field"
                  rows={3}
                  value={deviceProblem}
                  onChange={(e) => setDeviceProblem(e.target.value)}
                  placeholder="Describe the device problem"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Diagnosis
                </label>
                <textarea
                  className="input-field"
                  rows={3}
                  value={deviceDiagnosis}
                  onChange={(e) => setDeviceDiagnosis(e.target.value)}
                  placeholder="Technical diagnosis and findings"
                />
              </div>
            </div>
          </div>

          {/* Spare Part Items Section */}
          <div className="px-4 py-5 sm:px-6 border-t border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Spare Part Items</h3>

            <div className="flex justify-between items-center mb-4">
              <div></div>
              <div className="flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsItemModalOpen(true)}
                  className="bg-white hover:bg-gray-50 flex items-center"
                >
                  <svg className="h-4 w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Inventory Item
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsNonInventoryModalOpen(true)}
                  className="bg-white hover:bg-gray-50 flex items-center"
                >
                  <svg className="h-4 w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Non-Inventory Item
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Part
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                        No parts added yet. Use the buttons above to add parts.
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.item_name || 'Select part...'}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                item.item_type === 'inventory'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {item.item_type === 'inventory' ? 'Inventory' : 'Non-Inventory'}
                              </span>
                              {item.item_type === 'inventory' && item.sku && (
                                <span>SKU: {item.sku}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                              min="1"
                              max={item.item_type === 'inventory' ? item.available_stock : undefined}
                              required
                              className={`w-20 ${
                                item.item_type === 'inventory' &&
                                item.available_stock !== undefined &&
                                item.quantity > item.available_stock
                                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                                  : ''
                              }`}
                            />
                            {item.item_type === 'inventory' && item.available_stock !== undefined && (
                              <span className={`text-xs ${
                                item.quantity > item.available_stock
                                  ? 'text-red-600 font-medium'
                                  : 'text-gray-500'
                              }`}>
                                Available: {item.available_stock}
                                {item.quantity > item.available_stock && (
                                  <span className="ml-1">(Insufficient!)</span>
                                )}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', Number(e.target.value))}
                            min="0"
                            step="1000"
                            required
                            disabled={item.item_type === 'inventory'} // Non-editable for inventory items
                            className="w-32"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {formatCurrency(item.total_price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Remove item"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {items.length > 0 && (
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={3} className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                        Total Parts Cost
                      </td>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">
                        {formatCurrency(totalAmount)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Form Actions */}
          <div className="px-4 py-5 sm:px-6 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/repair-orders')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Repair Order'}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {/* Item Selection Modal */}
      <ItemSelectionModal
        isOpen={isItemModalOpen}
        onClose={() => setIsItemModalOpen(false)}
        onSelect={handleItemSelect}
        items={inventoryItems}
        selectedItemIds={items.map(item => item.item_id)}
        title="Select Spare Parts"
      />

      {/* Warning Modal */}
      <WarningModal
        isOpen={isWarningModalOpen}
        onClose={() => setIsWarningModalOpen(false)}
        title="Stock Warning"
        message={warningMessage}
        confirmText="Adjust Quantities"
        onConfirm={handleWarningConfirm}
      />

      {/* Non-Inventory Item Modal */}
      <NonInventoryItemModal
        isOpen={isNonInventoryModalOpen}
        onClose={() => setIsNonInventoryModalOpen(false)}
        onAdd={handleNonInventoryItemAdd}
      />
    </div>
  );
};

export default RepairOrderForm;